// Loaded with `defer`, so the DOM is ready as soon as this runs. Binding here
// rather than on DOMContentLoaded keeps search usable without waiting for the
// rest of the deferred scripts to settle.
(function () {
    const searchModal = {
        init: function () {
            this.modal = document.getElementById('SearchModal');
            if (!this.modal) return;

            this.input = document.getElementById('SearchModalInput');
            this.resultsContainer = document.getElementById('PredictiveSearchResults');
            this.initialContent = document.querySelector('.search-modal__initial-content');
            this.closeButtons = document.querySelectorAll('[data-search-modal-close]');
            this.searchTriggers = document.querySelectorAll('.js-search-header');
            this.spinner = this.modal.querySelector('.search-modal__loading-spinner');
            this.form = this.modal.querySelector('.search-modal__form');
            this.currentFocusIndex = -1;
            this.focusableResults = [];

            this.bindEvents();
            this.loadRecentlyViewed();
        },

        bindEvents: function () {
            // Open modal triggers
            this.searchTriggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.open();
                }, true);
            });

            // Close modal triggers
            this.closeButtons.forEach(button => {
                button.addEventListener('click', () => this.close());
            });

            // Prevent form submission on Enter (unless result is selected)
            this.form.addEventListener('submit', (e) => {
                e.preventDefault();
                // Only allow submission if a result is focused
                if (this.currentFocusIndex >= 0 && this.focusableResults[this.currentFocusIndex]) {
                    this.focusableResults[this.currentFocusIndex].click();
                }
                // Otherwise do nothing (stay in modal with instant results)
            });

            // Allow explicit button click to go to search page
            const submitButton = this.form.querySelector('.search-modal__submit');
            if (submitButton) {
                submitButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    const query = this.input.value.trim();
                    if (query) {
                        window.location.href = `/search?q=${encodeURIComponent(query)}&type=product`;
                    }
                });
            }

            // Input handling with instant search
            this.input.addEventListener('input', this.debounce((e) => {
                this.handleInput(e.target.value);
            }, 200)); // Reduced debounce for faster response

            // Keyboard navigation
            this.input.addEventListener('keydown', (e) => {
                this.handleKeyboardNavigation(e);
            });

            // Close on escape
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.isOpen()) {
                    this.close();
                }
            });

            // Popular search clicks (instant search)
            document.addEventListener('click', (e) => {
                if (e.target.closest('.search-modal__popular-link')) {
                    e.preventDefault();
                    const term = e.target.closest('.search-modal__popular-link').getAttribute('data-search-term');
                    if (term) {
                        this.input.value = term;
                        this.handleInput(term);
                        this.input.focus();
                    }
                }
            });
        },

        handleKeyboardNavigation: function (e) {
            this.updateFocusableResults();

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (this.focusableResults.length > 0) {
                        this.currentFocusIndex = Math.min(this.currentFocusIndex + 1, this.focusableResults.length - 1);
                        this.updateFocus();
                    }
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    this.currentFocusIndex = Math.max(this.currentFocusIndex - 1, -1);
                    if (this.currentFocusIndex === -1) {
                        this.input.focus();
                    } else {
                        this.updateFocus();
                    }
                    break;
                case 'Enter':
                    // Prevent default to stop form submission
                    // The form submit handler will handle the actual navigation
                    e.preventDefault();
                    if (this.currentFocusIndex >= 0 && this.focusableResults[this.currentFocusIndex]) {
                        this.focusableResults[this.currentFocusIndex].click();
                    }
                    // If no result is focused, do nothing (stay in modal)
                    break;
            }
        },

        updateFocusableResults: function () {
            if (this.resultsContainer.style.display !== 'none') {
                this.focusableResults = Array.from(this.resultsContainer.querySelectorAll('a[href], .predictive-search__item'));
            } else {
                this.focusableResults = [];
            }
        },

        updateFocus: function () {
            this.focusableResults.forEach((el, index) => {
                if (index === this.currentFocusIndex) {
                    el.focus();
                    el.style.outline = '2px solid #000';
                    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                } else {
                    el.style.outline = '';
                }
            });
        },

        open: function () {
            this.modal.classList.add('is-active');
            this.modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                this.input.focus();
                this.loadRecentlyViewed(); // Refresh on open
            }, 100);
        },

        close: function () {
            this.modal.classList.remove('is-active');
            this.modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            this.input.value = '';
            this.resultsContainer.style.display = 'none';
            this.resultsContainer.innerHTML = '';
            this.initialContent.style.display = 'block';
            this.currentFocusIndex = -1;
        },

        isOpen: function () {
            return this.modal.classList.contains('is-active');
        },

        handleInput: function (query) {
            const trimmedQuery = query.trim();

            if (trimmedQuery.length === 0) {
                this.resultsContainer.style.display = 'none';
                this.resultsContainer.innerHTML = '';
                this.initialContent.style.display = 'block';
                if (this.spinner) this.spinner.style.display = 'none';
                this.currentFocusIndex = -1;
                return;
            }

            // Show instant results for better UX
            if (trimmedQuery.length >= 2) {
                this.fetchResults(trimmedQuery);
            }
        },

        fetchResults: function (query) {
            // Use predictive search API with fields parameter to include SKU search
            const searchFields = 'title,body,product_type,variants.title,variants.sku,vendor,tag';
            const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][fields]=${searchFields}`;

            if (this.spinner) this.spinner.style.display = 'block';

            fetch(searchUrl)
                .then(response => response.json())
                .then(data => {
                    const products = data.resources?.results?.products || [];

                    if (products.length > 0) {
                        this.renderSearchResults(products, query);
                        this.resultsContainer.style.display = 'block';
                        this.initialContent.style.display = 'none';
                    } else {
                        this.showNoResults(query);
                    }
                    this.currentFocusIndex = -1;
                })
                .catch(error => {
                    console.error('Error fetching search results:', error);
                    this.showNoResults(query);
                })
                .finally(() => {
                    if (this.spinner) this.spinner.style.display = 'none';
                });
        },

        renderSearchResults: function (products, query) {
            const productsHTML = products.map(product => this.createSearchProductCard(product)).join('');

            this.resultsContainer.innerHTML = `
                <div class="search-modal__search-results">
                    <h3 class="search-modal__section-title">Products</h3>
                    <div class="search-modal__products-grid">
                        ${productsHTML}
                    </div>
                    <div class="search-modal__view-all">
                        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="btn btn--secondary btn--full-width">
                            View all results for "${this.escapeHtml(query)}"
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style="margin-left: 8px;">
                                <path d="M8.293 1.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L13.586 8 8.293 2.707a1 1 0 010-1.414z"/>
                            </svg>
                        </a>
                    </div>
                </div>
            `;
        },

        createSearchProductCard: function (product) {
            const price = parseFloat(product.price);
            const comparePrice = parseFloat(product.compare_at_price) || 0;
            const hasDiscount = comparePrice > price;
            const image = product.image || product.featured_image || '';

            return `
                <div class="search-modal__product-card">
                    <a href="${product.url}" class="search-modal__product-link">
                        <div class="search-modal__product-image">
                            ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : '<div class="search-modal__product-image-placeholder"></div>'}
                        </div>
                        <div class="search-modal__product-info">
                            <a href="${product.url}" class="grid-product__title">${this.escapeHtml(product.title)}</a>
                            <div class="grid-product__price">
                                ${hasDiscount ? `<span class="grid-product__price--original">$${comparePrice.toFixed(2)}</span>` : ''}
                                <span ${hasDiscount ? 'class="grid-product__price--highlight"' : ''}>$${price.toFixed(2)}</span>
                            </div>
                        </div>
                    </a>
                </div>
            `;
        },

        showNoResults: function (query) {
            this.resultsContainer.innerHTML = `
                <div class="search-modal__no-results">
                    <div class="search-modal__no-results-icon">
                        <svg viewBox="0 0 24 24" width="48" height="48" stroke="currentColor" stroke-width="2" fill="none">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </div>
                    <h3>No results found for "${this.escapeHtml(query)}"</h3>
                    <p>Try searching with different keywords or browse our collections.</p>
                    <div class="search-modal__no-results-actions">
                        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="btn btn--secondary">Try full search</a>
                        <a href="/collections/all" class="btn btn--primary">Browse All Products</a>
                    </div>
                </div>
            `;
            this.resultsContainer.style.display = 'block';
            this.initialContent.style.display = 'none';
        },

        // Recently Viewed Products Integration
        loadRecentlyViewed: function () {
            const recentlyViewedSection = document.getElementById('SearchModalRecentlyViewed');
            const recentlyViewedGrid = document.getElementById('SearchModalRecentlyViewedGrid');

            if (!recentlyViewedSection || !recentlyViewedGrid) return;

            // Get recently viewed from localStorage
            const recentlyViewed = this.getRecentlyViewedProducts();

            if (recentlyViewed.length === 0) {
                recentlyViewedSection.style.display = 'none';
                return;
            }

            // Fetch product data for recently viewed
            this.fetchRecentlyViewedProducts(recentlyViewed).then(products => {
                if (products.length > 0) {
                    recentlyViewedGrid.innerHTML = products.map(product => this.createProductCard(product)).join('');
                    recentlyViewedSection.style.display = 'block';
                } else {
                    recentlyViewedSection.style.display = 'none';
                }
            });
        },

        getRecentlyViewedProducts: function () {
            try {
                const viewed = localStorage.getItem('recentlyViewed');
                return viewed ? JSON.parse(viewed).slice(0, 4) : [];
            } catch (e) {
                return [];
            }
        },

        fetchRecentlyViewedProducts: function (handles) {
            // Fetch product data using Shopify's predictive search API
            const promises = handles.map(handle =>
                fetch(`/products/${handle}.js`)
                    .then(res => res.json())
                    .catch(() => null)
            );

            return Promise.all(promises).then(products => products.filter(p => p !== null));
        },

        createProductCard: function (product) {
            const price = product.price / 100;
            const comparePrice = product.compare_at_price / 100;
            const hasDiscount = comparePrice > price;
            const image = product.featured_image || '';

            return `
                <div class="search-modal__product-card">
                    <a href="/products/${product.handle}" class="search-modal__product-link">
                        <div class="search-modal__product-image">
                            ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="400" height="400">` : '<div class="search-modal__product-image-placeholder"></div>'}
                        </div>
                        <div class="search-modal__product-info">
                            <a href="/products/${product.handle}" class="grid-product__title">${this.escapeHtml(product.title)}</a>
                            <div class="grid-product__price">
                                ${hasDiscount ? `<span class="grid-product__price--original">$${comparePrice.toFixed(2)}</span>` : ''}
                                <span ${hasDiscount ? 'class="grid-product__price--highlight"' : ''}>$${price.toFixed(2)}</span>
                            </div>
                        </div>
                    </a>
                </div>
            `;
        },

        escapeHtml: function (text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        },

        debounce: function (func, wait) {
            let timeout;
            return function (...args) {
                const context = this;
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(context, args), wait);
            };
        }
    };

    searchModal.init();

    // Track product views for recently viewed functionality
    if (window.location.pathname.includes('/products/')) {
        const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
        if (productHandle) {
            try {
                let recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
                recentlyViewed = recentlyViewed.filter(h => h !== productHandle);
                recentlyViewed.unshift(productHandle);
                recentlyViewed = recentlyViewed.slice(0, 10); // Keep last 10
                localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
            } catch (e) {
                console.error('Error tracking recently viewed:', e);
            }
        }
    }
})();
